import React, { memo, useEffect, useState } from 'react';
import { Route, Redirect } from 'react-router';

import { ClientModel, GeneralModel, UserModel } from '../../../models';

import Main from '../Main';
import Loader from '../Loader';
import { CompanyStatus } from 'modules/models/resource';
import { hasValidPermissions } from 'modules/models/user';
import { ROUTES } from '../../../../constants';

export interface IProtectedRouteProps {
  location?: { pathname: string };
  authenticated: boolean;
  currentUserPermissions: UserModel.IPermission[];
  permissionsExpression?: string;
  clientMap?: GeneralModel.IEntityMap<ClientModel.IClient>;
  path: string;
  exact?: boolean;
  sessionChecked: boolean;
  currentCompanyId?: string;
  companyUserId?: string;
  render: any;
  tabMap?: any;
  isFcaUser: boolean;
  shouldBeFcaUser?: boolean;
  includeScopedPermissions?: boolean;
  featureMap: any;
  fetchingUserPermissions?: GeneralModel.ILoadingStatus;
  fetchClientLoading?: GeneralModel.ILoadingStatus;
  passAuthInfo?: boolean;
  session?: any;
  failedTransactions: any;
  fetchClient: (id: string) => void;
  fetchAdminPermission: (id: string) => void;
  recoverSession: () => void;
  getAccountData: () => void;
  fetchFeatureList: () => void;
  fetchFailedTransactions: (companyId: string) => void;
}

const ProtectedRoute = ({
  path,
  exact,
  tabMap,
  authenticated,
  currentUserPermissions = [],
  permissionsExpression,
  render: RenderComponent,
  sessionChecked,
  clientMap,
  currentCompanyId,
  companyUserId,
  isFcaUser,
  shouldBeFcaUser,
  includeScopedPermissions,
  fetchingUserPermissions,
  fetchClientLoading,
  passAuthInfo,
  session,
  failedTransactions,
  fetchClient,
  fetchAdminPermission,
  recoverSession,
  getAccountData,
  fetchFeatureList,
  fetchFailedTransactions,
}: IProtectedRouteProps) => {
  const [currentClient, setCurrentClient] = React.useState(ClientModel.getFallbackClient());
  const [isCompanyDataLoading, setIsCompanyDataLoading] = useState(authenticated);

  const hasCurrentUserPermissions = currentUserPermissions.length > 0;
  const LoginComponent = React.lazy(() => import('../Login'));
  const isGuest =
    !!currentCompanyId && (currentClient.status === CompanyStatus.ONBOARDING || (currentClient.status === CompanyStatus.ACTIVE && !currentClient.isVerified));

  useEffect(() => {
    if (
      (!currentClient?.id && clientMap[currentCompanyId]?.id) ||
      (currentClient?.status === CompanyStatus.ONBOARDING && clientMap[currentCompanyId]?.status === CompanyStatus.ACTIVE) ||
      (!currentClient?.isVerified && clientMap[currentCompanyId]?.isVerified)
    ) {
      setCurrentClient(clientMap[currentCompanyId]);
    }
  }, [clientMap, currentCompanyId, currentClient]);

  useEffect(() => {
    if (!currentClient?.id && currentCompanyId) {
      if (!isCompanyDataLoading) {
        setIsCompanyDataLoading(true);
      }
      fetchClient(currentCompanyId);
    }
  }, [fetchClient, currentCompanyId, currentClient]);

  useEffect(() => {
    if (isCompanyDataLoading && fetchClientLoading?.isLoading === false) {
      setIsCompanyDataLoading(false);
    }
  }, [fetchClientLoading, isCompanyDataLoading]);

  useEffect(() => {
    if (clientMap[currentCompanyId]?.status === CompanyStatus.ONBOARDING_PENDING_APPROVAL) {
      setCurrentClient(prev => ({
        ...prev,
        status: CompanyStatus.ONBOARDING_PENDING_APPROVAL,
      }));
    }
  }, [setCurrentClient, clientMap, currentCompanyId]);

  useEffect(() => {
    if (companyUserId) {
      fetchAdminPermission(companyUserId);
    }
  }, [companyUserId, fetchAdminPermission]);

  useEffect(() => {
    if (!authenticated) return;
    getAccountData();
  }, [authenticated, getAccountData]);

  useEffect(() => {
    /* istanbul ignore else */
    if (authenticated && hasCurrentUserPermissions) {
      fetchFeatureList();
    }
  }, [fetchFeatureList, authenticated, hasCurrentUserPermissions]);

  useEffect(() => {
    /* istanbul ignore else */
    if (!sessionChecked) recoverSession();
  }, [sessionChecked, recoverSession]);

  useEffect(() => {
    if (!failedTransactions && currentCompanyId) {
      fetchFailedTransactions(currentCompanyId);
    }
  }, [failedTransactions, currentCompanyId]);

  const onBoardingURL = `/client-onboarding/${currentCompanyId}`;
  const rootUrl = '/';
  return (
    <Route
      path={path}
      exact={exact}
      render={({ history, match }) => {
        const { params } = match;
        const { search } = history.location;
        const searchParams = new URLSearchParams(search);
        const finalPermissionsExpression =
          params.step && tabMap ? `${permissionsExpression} AND ${tabMap?.[params?.step]?.permissionsExpression}` : permissionsExpression;
        const hasAccess =
          hasValidPermissions(
            finalPermissionsExpression,
            currentUserPermissions,
            history.location.pathname.includes('/projects') && !includeScopedPermissions
              ? params.id
              : history.location.pathname.includes('/clients') && !includeScopedPermissions
              ? searchParams.get('projectId')
              : '',
            (history.location.pathname.includes('/projects') || history.location.pathname.includes('/clients')) && !includeScopedPermissions ? 'Project' : '',
            includeScopedPermissions
          ) && (shouldBeFcaUser ? isFcaUser : true);

        if ((authenticated && (!hasCurrentUserPermissions || isCompanyDataLoading)) || fetchingUserPermissions?.isLoading) return <Loader />;

        if (hasCurrentUserPermissions && !hasAccess) return <Redirect to="/unauthorised" />;

        if (!authenticated && !hasCurrentUserPermissions)
          return (
            <React.Suspense fallback={<Loader />}>
              <LoginComponent />
            </React.Suspense>
          );
        if (localStorage.getItem('Application-Mode') === 'TABLETEXPERIENCE' && path.toLowerCase().indexOf(ROUTES.TABLET_EXPERIENCE.path) < 0) {
          return <Redirect to={ROUTES.TABLET_EXPERIENCE.path} />;
        }
        if (
          currentClient &&
          (currentClient.status === CompanyStatus.ONBOARDING || (currentClient.status === CompanyStatus.ACTIVE && !currentClient.isVerified)) &&
          !history.location.pathname.includes('/client-onboarding')
        )
          return <Redirect to={onBoardingURL} />;

        if (currentClient && currentClient.status === CompanyStatus.ONBOARDING_PENDING_APPROVAL && history.location.pathname.includes('/client-onboarding'))
          return <Redirect to={rootUrl} />;

        if (
          currentClient &&
          currentClient.status === CompanyStatus.ACTIVE &&
          currentClient.isVerified &&
          history.location.pathname.includes('/client-onboarding')
        )
          return <Redirect to={currentClient?.projectsCount > 0 ? '/projects' : rootUrl} />;

        if (path.toLowerCase().indexOf(ROUTES.TABLET_EXPERIENCE.path) >= 0) {
          return (
            <React.Suspense fallback={<Loader />}>
              <RenderComponent {...(passAuthInfo ? { authInfo: { ...session, isFcaUser: isFcaUser } } : {})} />
            </React.Suspense>
          );
        } else {
          return (
            <Main isGuest={isGuest} isFcaUser={isFcaUser} failedTransactions={failedTransactions}>
              <React.Suspense fallback={<Loader />}>
                <RenderComponent {...(passAuthInfo ? { authInfo: { ...session, isFcaUser: isFcaUser } } : {})} />
              </React.Suspense>
            </Main>
          );
        }
      }}
    />
  );
};

export default memo(ProtectedRoute);
